import type { Sequence } from "../types/Sequence"
import { Reduce } from "./Reduce"

const gen = function* <T>(seq: Sequence<T>) {
  yield* seq
}

/**
 * Zips multiple sequences together into tuples.
 * Stops when the shortest sequence is exhausted.
 * Type-safe with proper tuple typing.
 *
 * @param args - Sequences to zip together
 * @returns Generator yielding tuples of corresponding elements
 *
 * @example
 * ```ts
 * import { Zip } from "@pdfking/tfunc"
 *
 * const names = ["Alice", "Bob", "Charlie"]
 * const ages = [25, 30, 35]
 * const cities = ["NYC", "LA", "CHI"]
 *
 * const zipped = [...Zip(names, ages, cities)]
 * // [["Alice", 25, "NYC"], ["Bob", 30, "LA"], ["Charlie", 35, "CHI"]]
 *
 * // Stops at shortest sequence
 * [...Zip([1, 2, 3], ["a", "b"])]
 * // [[1, "a"], [2, "b"]]
 * ```
 */
export const Zip = function* <T extends Sequence<unknown>[]>(
  ...args: T
): Iterable<{ [K in keyof T]: T[K] extends Sequence<infer U> ? U : never }> {
  if (args.length === 0) return

  // Convert all sequences to iterators
  const iterators = args.map(gen)

  while (true) {
    const results = iterators.map(iter => iter.next())

    // Stop if any iterator is done
    const done = Reduce(
      (acc, result) => acc || result.done || false,
      results,
      false
    )

    if (done) break

    yield results.map(result => result.value) as any
  }
}
